import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, GitBranch, Copy } from 'lucide-react';
import { useRules, useCreateRule, useEventTypes } from '../../../hooks/useRules';
import { getEventTypeStyle } from '../../../utils/colors';
import { formatRuleTiming } from '../../../utils/formatters';
import { useToast } from '../../../contexts/ToastContext';

function EmailRuleList() {
  const navigate = useNavigate();
  const toast = useToast();
  const [eventTypeFilter, setEventTypeFilter] = useState('All');
  const [activeFilter, setActiveFilter] = useState('All');
  const createMutation = useCreateRule();

  const { eventTypes } = useEventTypes();

  const filters = {};
  if (eventTypeFilter !== 'All') filters.eventType = eventTypeFilter;
  if (activeFilter !== 'All') filters.isActive = activeFilter === 'Active' ? 'true' : 'false';

  const { rules, total, isLoading, error } = useRules(filters);

  const handleRowClick = useCallback((id) => {
    navigate(`/admin/email/rules/${id}`);
  }, [navigate]);

  const getEventLabel = useCallback((value) => {
    const found = eventTypes.find((e) => e.value === value);
    return found ? found.label : value;
  }, [eventTypes]);

  const formatSendWindow = (rule) => {
    if (!rule.send_window_start || !rule.send_window_end) return 'Always';
    const tz = rule.send_window_tz === 'guest' ? 'Guest TZ' : rule.send_window_tz || 'UTC';
    return `${rule.send_window_start} - ${rule.send_window_end} (${tz})`;
  };

  const handleDuplicate = useCallback(async (e, rule) => {
    e.stopPropagation();
    try {
      const result = await createMutation.mutateAsync({
        name: `${rule.name} (Copy)`,
        event_type: rule.event_type,
        template_id: rule.template_id,
        delay_minutes: rule.delay_minutes,
        condition_expr: rule.condition_expr || null,
        dedupe_key_tpl: rule.dedupe_key_tpl || '',
        send_window_start: rule.send_window_start || null,
        send_window_end: rule.send_window_end || null,
        send_window_tz: rule.send_window_tz || null,
      });
      toast.success('Rule duplicated');
      navigate(`/admin/email/rules/${result.data.id}`);
    } catch (err) {
      toast.error('Failed to duplicate rule');
    }
  }, [createMutation, navigate, toast]);

  return (
    <div className="email-page">
      <div className="email-page-header">
        <h3>Automation Rules</h3>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/admin/email/rules/new')}
        >
          <Plus size={16} />
          New Rule
        </button>
      </div>

      <div className="email-filter-bar">
        <select
          className="email-filter-select"
          value={eventTypeFilter}
          onChange={(e) => setEventTypeFilter(e.target.value)}
        >
          <option value="All">All Event Types</option>
          {eventTypes.map((et) => (
            <option key={et.value} value={et.value}>{et.label}</option>
          ))}
        </select>
        <select
          className="email-filter-select"
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value)}
        >
          <option value="All">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      {error && (
        <div className="email-error">Failed to load rules. Please try again.</div>
      )}

      {isLoading ? (
        <div className="email-table-skeleton">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton-row" />
          ))}
        </div>
      ) : rules.length === 0 ? (
        <div className="email-empty-state">
          <GitBranch size={48} strokeWidth={1} />
          <p>No rules found.</p>
          <p className="text-muted">
            {eventTypeFilter !== 'All' || activeFilter !== 'All'
              ? 'Try adjusting your filters.'
              : 'Create your first automation rule to get started.'}
          </p>
        </div>
      ) : (
        <>
          <div className="email-table-wrapper">
            <table className="email-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Event Type</th>
                  <th>Template</th>
                  <th>Timing</th>
                  <th>Send Window</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr
                    key={rule.id}
                    className="email-table-row"
                    onClick={() => handleRowClick(rule.id)}
                  >
                    <td className="email-table-name">{rule.name}</td>
                    <td>
                      <span
                        className="email-event-badge"
                        style={getEventTypeStyle(rule.event_type)}
                      >
                        {getEventLabel(rule.event_type)}
                      </span>
                    </td>
                    <td className="email-table-template">
                      {rule.template_name || '(deleted)'}
                    </td>
                    <td>{formatRuleTiming(rule.delay_minutes, rule.event_type)}</td>
                    <td className="email-table-date">{formatSendWindow(rule)}</td>
                    <td>
                      <span className={`email-status-dot ${rule.is_active ? 'active' : 'inactive'}`} />
                      {rule.is_active ? 'Active' : 'Inactive'}
                    </td>
                    <td>
                      <button
                        className="cb-remove-btn"
                        title="Duplicate"
                        onClick={(e) => handleDuplicate(e, rule)}
                      >
                        <Copy size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="email-table-footer">
            {total} rule{total !== 1 ? 's' : ''}
          </div>
        </>
      )}
    </div>
  );
}

export default EmailRuleList;
